/**
 * testDataGenerator.ts
 * Genera XMLs de facturas SUNAT (UBL 2.1) ficticias para testing.
 * RUC emisor ficticio: 20999999999 (empresa "Demo Contable SAC")
 *
 * USO (consola del navegador o Node):
 *   import { generateTestXmls } from './testDataGenerator';
 *   generateTestXmls(); // descarga 5 archivos XML
 */

interface InvoiceParams {
  tipo: '01' | '03' | '07';     // 01=Factura, 03=Boleta, 07=NC
  serie: string;
  correlativo: string;
  fechaEmision: string;         // YYYY-MM-DD
  rucEmisor: string;
  razonSocialEmisor: string;
  rucReceptor: string;
  razonSocialReceptor: string;
  baseImponible: number;        // Valor de venta sin IGV
  igv: number;
  total: number;
  descripcion: string;
}

function buildFacturaXml(p: InvoiceParams): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
  xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
  xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2"
  xmlns:ext="urn:oasis:names:specification:ubl:schema:xsd:CommonExtensionComponents-2">
  <cbc:UBLVersionID>2.1</cbc:UBLVersionID>
  <cbc:CustomizationID>2.0</cbc:CustomizationID>
  <cbc:ID>${p.serie}-${p.correlativo.padStart(8, '0')}</cbc:ID>
  <cbc:IssueDate>${p.fechaEmision}</cbc:IssueDate>
  <cbc:InvoiceTypeCode listID="0101">${p.tipo}</cbc:InvoiceTypeCode>
  <cbc:DocumentCurrencyCode>PEN</cbc:DocumentCurrencyCode>
  <cac:AccountingSupplierParty>
    <cac:Party>
      <cac:PartyIdentification>
        <cbc:ID schemeID="6">${p.rucEmisor}</cbc:ID>
      </cac:PartyIdentification>
      <cac:PartyName>
        <cbc:Name>${p.razonSocialEmisor}</cbc:Name>
      </cac:PartyName>
    </cac:Party>
  </cac:AccountingSupplierParty>
  <cac:AccountingCustomerParty>
    <cac:Party>
      <cac:PartyIdentification>
        <cbc:ID schemeID="6">${p.rucReceptor}</cbc:ID>
      </cac:PartyIdentification>
      <cac:PartyName>
        <cbc:Name>${p.razonSocialReceptor}</cbc:Name>
      </cac:PartyName>
    </cac:Party>
  </cac:AccountingCustomerParty>
  <cac:TaxTotal>
    <cbc:TaxAmount currencyID="PEN">${p.igv.toFixed(2)}</cbc:TaxAmount>
    <cac:TaxSubtotal>
      <cbc:TaxableAmount currencyID="PEN">${p.baseImponible.toFixed(2)}</cbc:TaxableAmount>
      <cbc:TaxAmount currencyID="PEN">${p.igv.toFixed(2)}</cbc:TaxAmount>
      <cac:TaxCategory>
        <cbc:ID>S</cbc:ID>
        <cbc:Percent>18</cbc:Percent>
        <cac:TaxScheme>
          <cbc:ID>1000</cbc:ID>
          <cbc:Name>IGV</cbc:Name>
          <cbc:TaxTypeCode>VAT</cbc:TaxTypeCode>
        </cac:TaxScheme>
      </cac:TaxCategory>
    </cac:TaxSubtotal>
  </cac:TaxTotal>
  <cac:LegalMonetaryTotal>
    <cbc:LineExtensionAmount currencyID="PEN">${p.baseImponible.toFixed(2)}</cbc:LineExtensionAmount>
    <cbc:TaxInclusiveAmount currencyID="PEN">${p.total.toFixed(2)}</cbc:TaxInclusiveAmount>
    <cbc:PayableAmount currencyID="PEN">${p.total.toFixed(2)}</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>
  <cac:InvoiceLine>
    <cbc:ID>1</cbc:ID>
    <cbc:InvoicedQuantity unitCode="NIU">1</cbc:InvoicedQuantity>
    <cbc:LineExtensionAmount currencyID="PEN">${p.baseImponible.toFixed(2)}</cbc:LineExtensionAmount>
    <cac:Item>
      <cbc:Description>${p.descripcion}</cbc:Description>
    </cac:Item>
    <cac:Price>
      <cbc:PriceAmount currencyID="PEN">${p.baseImponible.toFixed(2)}</cbc:PriceAmount>
    </cac:Price>
  </cac:InvoiceLine>
</Invoice>`;
}

// RUC ficticio de la empresa demo (receptor de compras / emisor de ventas)
const RUC_DEMO = '20999999999';
const NOMBRE_DEMO = 'DEMO CONTABLE SAC';

// ── FACTURAS DE COMPRAS (proveedores → Demo Contable SAC) ──────────────────

export const TEST_COMPRAS: InvoiceParams[] = [
  {
    tipo: '01',
    serie: 'F001',
    correlativo: '00000101',
    fechaEmision: '2025-01-05',
    rucEmisor: '20100070970',
    razonSocialEmisor: 'TELEFONICA DEL PERU SAA',
    rucReceptor: RUC_DEMO,
    razonSocialReceptor: NOMBRE_DEMO,
    baseImponible: 847.46,
    igv: 152.54,
    total: 1000.00,
    descripcion: 'Servicio de telefonía e internet empresarial — enero 2025',
  },
  {
    tipo: '01',
    serie: 'F002',
    correlativo: '00000215',
    fechaEmision: '2025-01-10',
    rucEmisor: '20492092313',
    razonSocialEmisor: 'SUMINISTROS PERU SAC',
    rucReceptor: RUC_DEMO,
    razonSocialReceptor: NOMBRE_DEMO,
    baseImponible: 4237.29,
    igv: 762.71,
    total: 5000.00,
    descripcion: 'Mercaderías — uniformes y material de oficina',
  },
  {
    tipo: '01',
    serie: 'E001',
    correlativo: '00000033',
    fechaEmision: '2025-01-15',
    rucEmisor: '20601234567',
    razonSocialEmisor: 'CONTADORES & ASESORES SAC',
    rucReceptor: RUC_DEMO,
    razonSocialReceptor: NOMBRE_DEMO,
    baseImponible: 2542.37,
    igv: 457.63,
    total: 3000.00,
    descripcion: 'Servicio de asesoría contable y tributaria — enero 2025',
  },
  {
    tipo: '01',
    serie: 'F001',
    correlativo: '00001880',
    fechaEmision: '2025-01-20',
    rucEmisor: '20131312955',
    razonSocialEmisor: 'BANCO DE CREDITO DEL PERU',
    rucReceptor: RUC_DEMO,
    razonSocialReceptor: NOMBRE_DEMO,
    baseImponible: 423.73,
    igv: 76.27,
    total: 500.00,
    descripcion: 'Comisiones y portes bancarios — enero 2025',
  },
  {
    tipo: '01',
    serie: 'F003',
    correlativo: '00000456',
    fechaEmision: '2025-01-25',
    rucEmisor: '20555666777',
    razonSocialEmisor: 'ALQUILERES LIMA SAC',
    rucReceptor: RUC_DEMO,
    razonSocialReceptor: NOMBRE_DEMO,
    baseImponible: 2118.64,
    igv: 381.36,
    total: 2500.00,
    descripcion: 'Arrendamiento local comercial — enero 2025',
  },
];

// ── FACTURAS DE VENTAS (Demo Contable SAC → clientes) ────────────────────────

export const TEST_VENTAS: InvoiceParams[] = [
  {
    tipo: '01',
    serie: 'F001',
    correlativo: '00000001',
    fechaEmision: '2025-01-08',
    rucEmisor: RUC_DEMO,
    razonSocialEmisor: NOMBRE_DEMO,
    rucReceptor: '20345678901',
    razonSocialReceptor: 'CLIENTE MAYORISTA SA',
    baseImponible: 8474.58,
    igv: 1525.42,
    total: 10000.00,
    descripcion: 'Venta de mercaderías — lote enero 2025',
  },
  {
    tipo: '01',
    serie: 'F001',
    correlativo: '00000002',
    fechaEmision: '2025-01-18',
    rucEmisor: RUC_DEMO,
    razonSocialEmisor: NOMBRE_DEMO,
    rucReceptor: '20456789012',
    razonSocialReceptor: 'DISTRIBUIDOR NORTE SAC',
    baseImponible: 5932.20,
    igv: 1067.80,
    total: 7000.00,
    descripcion: 'Venta de mercaderías — distribución regional',
  },
  {
    tipo: '01',
    serie: 'F001',
    correlativo: '00000003',
    fechaEmision: '2025-01-28',
    rucEmisor: RUC_DEMO,
    razonSocialEmisor: NOMBRE_DEMO,
    rucReceptor: '20567890123',
    razonSocialReceptor: 'COMERCIAL SUR EIRL',
    baseImponible: 3389.83,
    igv: 610.17,
    total: 4000.00,
    descripcion: 'Servicio de consultoría comercial — enero 2025',
  },
];

/**
 * Descarga todos los XMLs de prueba al navegador.
 * Llama desde DevTools → Console: generateTestXmls()
 */
export function generateTestXmls() {
  const all = [
    ...TEST_COMPRAS.map(p => ({ params: p, prefix: 'COMPRA' })),
    ...TEST_VENTAS.map(p => ({ params: p, prefix: 'VENTA' })),
  ];

  for (const { params: p } of all) {
    const xml = buildFacturaXml(p);
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // Nombre según patrón SUNAT: RUC-TipoDoc-Serie-Correlativo.xml
    a.download = `${p.rucEmisor}-${p.tipo}-${p.serie}-${p.correlativo}.xml`;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  console.log(`[QuiPus Test] Generados ${all.length} XMLs de prueba:`);
  all.forEach(({ params: p }) =>
    console.log(`  → ${p.rucEmisor}-${p.tipo}-${p.serie}-${p.correlativo}.xml  S/${p.total.toFixed(2)}`),
  );
}

// Exponer globalmente para uso desde DevTools Console
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).generateTestXmls = generateTestXmls;
}
